import { FC, useState } from "react";
import { Box, Heading, Stack, Button, Flex } from "@chakra-ui/react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TypeOf } from "zod";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { GET_SELF_DATA_URL } from "../../utils/endpoints";
import ResetPassword from "./ResetPassword";
import InputFieldComponent from "../../components/molecule/InputField/InputFieldComponent";
import { LoginItemSchema } from "./data/LoginItemSchema";
import { useAuth } from "../../provider/AuthProvider";
import LoadingButton from "../../components/atom/Button/LoadingButton/LoadingButton";

type LoginFormInputs = TypeOf<typeof LoginItemSchema>;

const Login: FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(LoginItemSchema),
  });

  const handleLogin: SubmitHandler<LoginFormInputs> = async () => {
    setIsLoaded(false);
    const data = getValues();
    try {
      const user = await login(data);
      if (user) {
        await api.get(`${GET_SELF_DATA_URL}?Id=${user.id}`);
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      console.error("Login failed", error);
    }
    setIsLoaded(true);
  };

  const handleShowResetPassword = () => setShowResetPassword(true);
  const handleBackToLogin = () => setShowResetPassword(false);

  if (showResetPassword) {
    return <ResetPassword isOpen={true} onBack={handleBackToLogin} />;
  }

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(handleLogin)}
      width="100%"
      display="flex"
      flexDirection="column"
      gap={8}
    >
      <Stack spacing={2}>
        <Heading as="h1">Autenticação</Heading>
        <InputFieldComponent
          label="Email do utilizador"
          name="email"
          type="text"
          register={register}
          errors={errors}
        />
        <InputFieldComponent
          label="Senha"
          name="password"
          type="password"
          register={register}
          errors={errors}
          isPassword
        />
        <Flex justifyContent="end">
          <Button variant="tertiary" onClick={handleShowResetPassword}>
            Esqueci-me da senha
          </Button>
        </Flex>
      </Stack>
      <LoadingButton type="submit" alignSelf="flex-start" isLoading={!isLoaded}>
        Entrar
      </LoadingButton>
    </Box>
  );
};

export default Login;
